import {Module, PubSub} from '@mamdev/cad.static-next-lib';
import Tracking2 from '../../ts/services/tracking2';

type SliderClickname = 'video-slideshow-prev' | 'video-slideshow-next' | 'video-slider-50' | 'video-slider-100' | 'prev-button' | 'next-button';

/**
 * Users can horizontally swipe through a list of video teasers.
 */
export default class VideoSlider extends Module {
  private observer: IntersectionObserver;
  private scrollDirection: 'left' | 'right' = 'right';
  private reached50 = false;
  private reached100 = false;
  private teaserContainer: HTMLElement;
  private prevButton: HTMLElement | null;
  private nextButton: HTMLElement | null;
  private totalTeasers = 0;
  private lastScrollAmount = 0;

  // True if the teasers are all shown in the page, instead of a carousel view.
  private readonly isExpanded;

  static attachTo(root: HTMLElement): VideoSlider {
    return new VideoSlider(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'video-slider';

    this.isExpanded = 'expanded' in this.root_.dataset;
  }

  start() {
    try {
      this.log('start');
      console.log('[video-slider]', 'isExpanded', this.isExpanded);

      const teaserContainerElement = this.root_.querySelector<HTMLElement>('.video-slider__teaser-container');

      if (!teaserContainerElement) return;

      this.teaserContainer = teaserContainerElement;
      this.totalTeasers = this.teaserContainer.childElementCount;

      this.prevButton = this.root_.querySelector<HTMLElement>('.video-slider__prev-button');
      this.nextButton = this.root_.querySelector<HTMLElement>('.video-slider__next-button');

      this.log('total teasers = ' + this.totalTeasers);

      this.initEventHandlers();

      if (!this.isExpanded) {
        this.setupIntersectionObserver();
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  initEventHandlers() {
    // this.log('init event handlers');

    if (!this.isExpanded) {
      this.teaserContainer.addEventListener('scroll', () => {
        if (this.teaserContainer.scrollLeft > this.lastScrollAmount) {
          this.scrollDirection = 'right';
        } else {
          this.scrollDirection = 'left';
        }

        this.lastScrollAmount = this.teaserContainer.scrollLeft;
      });

      this.prevButton?.addEventListener('click', () => {
        this.teaserContainer.scrollLeft -= this.teaserContainer.clientWidth;
        this.sendEventTracking('prev-button');
      });

      this.nextButton?.addEventListener('click', () => {
        this.teaserContainer.scrollLeft += this.teaserContainer.clientWidth;
        this.sendEventTracking('next-button');
      });
    }

    this.root_.addEventListener('click', event => {
      // this.log('click');

      try {
        event.preventDefault();

        const targetElement = (event.target as HTMLElement).closest('a') as HTMLElement;
        const videoIDs: number[] = [];

        // console.log('[video-slider] targetElement', targetElement);

        if (targetElement) {
          const teasers = Array.from(this.root_.querySelectorAll<HTMLElement>('.video-slider__vertical-teaser'));

          teasers.forEach(teaser => {
            if (teaser.dataset.coremid) videoIDs.push(parseInt(teaser.dataset.coremid));
          });

          console.log('[video-slider] video IDs', videoIDs);

          PubSub.publish('vertical-video:teaser-clicked', {
            targetElement,
            videoIDs
          });

          // BRAIN tracking
          const brainTrackingString = targetElement.dataset.brainCustomZone;

          // this.log('BRAIN clicktracking: ' + brainTrackingString);

          if (brainTrackingString) {
            const brainTrackingClickNames = brainTrackingString.split('.');

            const brainObject = {
              click1: brainTrackingClickNames[1],
              click2: brainTrackingClickNames[2],
              click3: brainTrackingClickNames[3],
              click4: brainTrackingClickNames[4]
            };

            this.publish('video-slider:brain-tracking', brainObject);
          }
        }
      } catch (e) {
        this.logUnknownError(e, true);
      }
    });

    if (this.teaserContainer.firstElementChild) {
      Tracking2.bindTrackingExposeHandler(this.teaserContainer.firstElementChild);
    }
  }

  setupIntersectionObserver() {
    // this.log('setup intersection observer');

    if (this.observer !== undefined) return;

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        const teaser = entry.target as HTMLElement;
        const teaserIndex = parseInt(teaser.dataset.index ?? '');

        // console.log('[video-slider] intersecting', teaserIndex, teaser);

        if (teaserIndex === 0) {
          this.prevButton?.classList.add('hidden');
        } else if (teaserIndex === this.totalTeasers - 1) {
          this.nextButton?.classList.add('hidden');
        } else if (this.lastScrollAmount > 0) {
          this.prevButton?.classList.remove('hidden');
          this.nextButton?.classList.remove('hidden');
        }

        if (!this.reached50 && teaserIndex >= (this.totalTeasers - 1) / 2) {
          this.reached50 = true;
          // this.log('50% reached');
          this.sendEventTracking('video-slider-50', teaserIndex, teaser);
        }

        if (!this.reached100 && teaserIndex >= this.totalTeasers - 1) {
          this.reached100 = true;
          // this.log('100% reached');
          this.sendEventTracking('video-slider-100', teaserIndex, teaser);
        }

        // this.log('teaserIndex = ' + teaserIndex);

        // Track every 3rd teaser seen.
        if ((teaserIndex + 1) % 3 === 0) {
          // this.log(`Track teaser ${teaserIndex + 1} (index ${teaserIndex})`);

          if (this.scrollDirection === 'right') {
            this.sendEventTracking('video-slideshow-next', teaserIndex, teaser);
            // this.log('SLIDE: next ' + teaserIndex);
          } else if (this.scrollDirection === 'left') {
            this.sendEventTracking('video-slideshow-prev', teaserIndex, teaser);
            // this.log('SLIDE: prev ' + teaserIndex);
          }
        }
      });
    }, {
      root: this.teaserContainer,
      rootMargin: '0px',
      threshold: 1
    });

    // init
    Array.from(this.teaserContainer.children).forEach(item => {
      this.observer.observe(item);
    });
  }

  sendEventTracking(clickname: SliderClickname, teaserIndex?: number, teaser?: HTMLElement) {
    // console.log('send event pixel', clickname);

    // Call TealiumLink for desktop button clicks
    if (['prev-button', 'next-button'].includes(clickname)) {
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentPath: 'video-slider.video-slider__' + clickname,
        elementTagName: 'button',
        eventType: 'mousedown'
      });
    } else {
      // Call TealiumLink for swipe
      Tracking2.callTealiumLink({
        eventType: 'swipe',
        componentName: teaser?.dataset.componentName,
        componentPath: teaser?.dataset.componentPath,
        componentResultState: clickname
      });
    }

    // Event pixel
    PubSub.publish('video-slider:setEventPixel', {
      piUrl: this.setCorrectUrlCategory(window.ui.brain.eventUrl),
      clickname: clickname,
      slidenumber: teaserIndex
    });
  }

  setCorrectUrlCategory(baseUrl: string) {
    const piUrl = new URL(baseUrl);
    piUrl.searchParams.set('category', 'videoslider');
    const nameParam = piUrl.searchParams.get('name');

    if (nameParam) {
      const nameArray = nameParam.split('.');
      nameArray[3] = 'videoslider';
      piUrl.searchParams.set('name', nameArray.join('.'));
    }

    return piUrl;
  }
}
